<template>
  <div class="power-grid-wrapper">

    <!-- Portada inicial con imagen de fondo -->
    <div 
      v-if="gameState === 'MENU'" 
      class="overlay menu-background" 
      :style="{ backgroundImage: 'url(/minigames/apagon/fondo.jpg)' }"
    >
      <div class="menu-content">
        <h1>Apagón Masivo</h1>
        <p v-if="modoPrueba" class="test-mode-msg">
          Modo prueba activo: no se guarda partida ni se da dinero real.
        </p>
        <p>Conecta el generador con la casa.<br>¡Toca las tuberías para reestablecer la red eléctrica!</p>
        <button class="btn-start" @click="startGame">EMPEZAR PARTIDA</button>
      </div>
    </div>

    <!-- HUD del juego -->
    <div v-if="gameState !== 'MENU' && gameState !== 'PARTIDA_TERMINADA'" class="hud">
      <div class="stat">PUNTOS: {{ score }}</div>
      <div class="stat" :class="{ 'timer-low': timeLeft <= 10 }">TIEMPO: {{ timeLeft }}s</div>
    </div>

    <div id="game-container" ref="gameContainer"></div>

    <div class="footer-controls">
       <button class="small-btn" @click="skipLevel" v-if="gameState === 'PLAYING'">PASO</button>
    </div>


    <!-- Portada final de la partida -->
    <div 
      v-if="gameState === 'PARTIDA_TERMINADA'" 
      class="overlay menu-background"
      :style="{ backgroundImage: 'url(/minigames/apagon/fondo.jpg)' }"
    >
      <div class="menu-content summary-content">        
        <h1>Apagón Masivo</h1>
        <p v-if="modoPrueba" class="test-mode-msg">
          Modo prueba activo: no se guarda partida ni se da dinero real.
        </p>

        <!-- Resumen de puntuación y stats -->
        <div class="summary-box">
          <h2>¡TIEMPO AGOTADO!</h2>
          <div class="summary-score">
            <span>PUNTOS TOTALES</span>
            <strong>{{ verifiedScore ?? score }}</strong>
          </div>

          <div v-if="verifiedMoney !== null" class="summary-money">
            💰 Ganado: <b>{{ Number(verifiedMoney).toLocaleString("es-ES") }} €</b>
          </div>

          <!-- Iconos de casitas por cada pantalla solucionada -->
          <div class="houses-container">
            <p class="houses-title">Casas reestablecidas: {{ statsInternas.pantallas }}</p>
            <div class="houses-grid">
              <span 
                v-for="n in statsInternas.pantallas" 
                :key="n" 
                class="house-icon"
                title="Casa con luz"
              >
                🏠
              </span>
              <p v-if="statsInternas.pantallas === 0" class="no-houses">
                No lograste reconectar ninguna casa a tiempo.
              </p>
            </div>
          </div>
        </div>

        <button class="btn-start" @click="$emit('volver-menu')">VOLVER AL MENÚ</button>
      </div>
    </div>

  </div>
</template>

<script>
import * as Phaser from 'phaser';
import axios from "@/axios";
import CryptoJS from 'crypto-js';
import { useGameStore } from "@/stores/game";
import Swal from "sweetalert2";

const TIME_LEFT_CONST = 60; 

export default {
  name: 'PowerGridPro',
  data() {
    return {
      modoPrueba: false,
      store: useGameStore(),
      verifiedMoney: null,
      game: null,
      scene: null,
      gameState: 'MENU',
      score: 0,
      timeLeft: TIME_LEFT_CONST,
      timerInterval: null,
      gridSize: 5,
      grid: [],
      idPartida: null,
      claveTemporal: null,
      verifiedScore: null,
      statsInternas: {
        pantallas: 0,
        clicks: 0,
        saltos: 0,
        startTime: null,
        totalTimeMs: 0
      }
    };
  },
  mounted() {
    this.initPhaser();
  },
  beforeUnmount() {
    this.cleanup();
  },
  methods: {
    initPhaser() {
      const self = this;
      const config = {
        type: Phaser.AUTO,
        pixelArt: true,
        parent: 'game-container',
        width: 350,
        height: 350,
        backgroundColor: '#0a0a12',
        scene: {
          preload() {
            this.load.spritesheet('spritesheet', '/minigames/apagon/sprites-apagon.png', {
              frameWidth: 128,
              frameHeight: 128
            });
          },
          create() {
            self.scene = this;
          }
        }
      };
      this.game = new Phaser.Game(config);
    },

    async startGame() {
      try {
        if (this.modoPrueba) {
          this.idPartida = null;
          this.claveTemporal = null;
        } else {
          const response = await axios.post('/api/minigames/partida/iniciar', { 
            game_id: 'apagon'        
          });
          
          if (response.data.status === 'success') {
            this.idPartida = response.data.data.id_partida;
            this.claveTemporal = response.data.data.clave_temporal;
          }
        }

        this.gameState = 'PLAYING';
        this.score = 0;
        this.timeLeft = TIME_LEFT_CONST;
        this.verifiedScore = null;
        this.verifiedMoney = null;
        this.statsInternas = { 
          pantallas: 0, clicks: 0, saltos: 0, 
          startTime: Date.now(), totalTimeMs: 0 
        };
        this.createLevel();
        this.startTimer();

      } catch (error) {
        console.error("Error iniciando partida", error);

        const msg =
          error?.response?.data?.message ||
          error?.response?.data?.data?.message ||
          "No se pudo iniciar la partida.";

        await Swal.fire({
          icon: "info",
          title: "Minijuego no disponible",
          text: msg,
          confirmButtonText: "Entendido",
        customClass: {
          confirmButton: "clausulazo-swal-confirm",
        },
        });

        this.gameState = "MENU";
      }
    },

    startTimer() {
      clearInterval(this.timerInterval);
      this.timerInterval = setInterval(() => {
        this.timeLeft--;
        if (this.timeLeft <= 0) this.endGame();
      }, 1000);
    },

    generateSolvablePath() {
      const size = this.gridSize;
      const visited = Array.from({ length: size }, () => Array(size).fill(false));
      const path = [];

      const dfs = (x, y) => {
        visited[y][x] = true;
        path.push({ x, y });

        if (x === size - 1 && y === size - 1) return true;

        const neighbors = [
          { x: x + 1, y },
          { x: x - 1, y },
          { x, y: y + 1 },
          { x, y: y - 1 }
        ].filter(n => n.x >= 0 && n.x < size && n.y >= 0 && n.y < size && !visited[n.y][n.x]);

        Phaser.Utils.Array.Shuffle(neighbors);

        for (const next of neighbors) {
          if (dfs(next.x, next.y)) return true;
        }

        path.pop();
        return false;
      };

      dfs(0, 0);
      return path;
    },

    createLevel() {
      if (!this.scene) return;
      this.scene.children.removeAll();
      this.grid = [];
      const step = 70; 
      const startX = step / 2; 
      const startY = step / 2; 

      const FRAME_BG = 0;
      const FRAME_GEN = 1;
      const FRAME_HOME = 2;
      const FRAME_WIRE_I = 3;
      const FRAME_WIRE_L = 4;

      const path = this.generateSolvablePath();
      const pathMap = {};
      path.forEach((pt, index) => {
        pathMap[`${pt.x},${pt.y}`] = index;
      });

      for (let y = 0; y < this.gridSize; y++) {
        this.grid[y] = [];
        for (let x = 0; x < this.gridSize; x++) {
          this.scene.add.sprite(startX + x * step, startY + y * step, 'spritesheet', FRAME_BG)
            .setDisplaySize(step, step)
            .setAlpha(0.6);

          let type = 'wire';
          let frame = FRAME_WIRE_I;
          let pieceType = 'I';

          if (x === 0 && y === 0) {
            type = 'gen';
            frame = FRAME_GEN;
          } else if (x === 4 && y === 4) {
            type = 'home';
            frame = FRAME_HOME;
          } else if (pathMap[`${x},${y}`] !== undefined) {
            const idx = pathMap[`${x},${y}`];
            const prev = path[idx - 1];
            const next = path[idx + 1];

            const dx1 = prev.x - x;
            const dy1 = prev.y - y;
            const dx2 = next.x - x;
            const dy2 = next.y - y;

            if (dx1 === -dx2 || dy1 === -dy2) {
              pieceType = 'I';
              frame = FRAME_WIRE_I;
            } else {
              pieceType = 'L';
              frame = FRAME_WIRE_L;
            }
          } else {
            pieceType = Math.random() > 0.5 ? 'I' : 'L';
            frame = pieceType === 'I' ? FRAME_WIRE_I : FRAME_WIRE_L;
          }

          let rotation = type === 'wire' ? Phaser.Math.RND.pick([0, 90, 180, 270]) : 0;

          const cellSprite = this.scene.add.sprite(startX + x * step, startY + y * step, 'spritesheet', frame)
            .setInteractive()
            .setAngle(rotation)
            .setDisplaySize(step, step);
          
          if (type === 'gen') {
            this.scene.tweens.add({
              targets: cellSprite,
              scaleX: 0.45,
              scaleY: 0.45,
              duration: 300,
              yoyo: true,
              repeat: -1,
              ease: 'Sine.easeInOut'
            });
          }

          if (type === 'wire') {
            cellSprite.on('pointerdown', () => this.rotatePiece(x, y));
          }

          this.grid[y][x] = { x, y, type, pieceType, rotation, sprite: cellSprite, powered: false };
        }
      }
      this.updateFlow();
    },

    rotatePiece(x, y) {
      if (this.gameState !== 'PLAYING') return;
      this.statsInternas.clicks++; 
      const cell = this.grid[y][x];
      cell.rotation = (cell.rotation + 90) % 360;
      cell.sprite.setAngle(cell.rotation);
      this.updateFlow();
    },

    getExits(cell) {
      if (cell.type === 'gen' || cell.type === 'home') return ['N', 'S', 'E', 'O'];
      const r = cell.rotation;
      if (cell.pieceType === 'I') {
        return (r % 180 === 0) ? ['N', 'S'] : ['E', 'O'];
      } else {
        if (r === 0) return ['S', 'E'];
        if (r === 90) return ['S', 'O'];
        if (r === 180) return ['N', 'O'];
        if (r === 270) return ['N', 'E'];
      }
      return [];
    },

    updateFlow() {
      this.grid.flat().forEach(c => { c.powered = false; });

      const queue = [this.grid[0][0]];
      this.grid[0][0].powered = true;
      let head = 0;
      
      while (head < queue.length) {
        const curr = queue[head++];
        const exits = this.getExits(curr);
        const directions = [
          { dir: 'N', dx: 0, dy: -1, opp: 'S' },
          { dir: 'S', dx: 0, dy: 1, opp: 'N' },
          { dir: 'E', dx: 1, dy: 0, opp: 'O' },
          { dir: 'O', dx: -1, dy: 0, opp: 'E' }
        ];

        directions.forEach(d => {
          const nx = curr.x + d.dx, ny = curr.y + d.dy;
          if (nx >= 0 && nx < this.gridSize && ny >= 0 && ny < this.gridSize) {
            const next = this.grid[ny][nx];
            if (!next.powered && exits.includes(d.dir)) {
              if (this.getExits(next).includes(d.opp)) {
                next.powered = true;
                queue.push(next);
              }
            }
          }
        });
      }

      this.grid.flat().forEach(c => {
        if (c.type === 'gen') {
          c.sprite.clearTint();
        } else if (c.powered) {
          c.sprite.clearTint();
        } else {
          c.sprite.setTint(0x555555);
        }
      });

      const home = this.grid[4][4];
      if (home.powered) {
        this.statsInternas.pantallas++; 
        this.score += 100;
        this.gameState = 'LOCKED';
      
        this.showSuccessText();

        setTimeout(() => { 
          if (this.gameState !== 'GAMEOVER' && this.gameState !== 'PARTIDA_TERMINADA') {
            this.gameState = 'PLAYING';
            this.createLevel(); 
          }
        }, 1600);
      }
    },

    showSuccessText() {
      if (!this.scene) return;

      const pointsText = this.scene.add.text(175, 140, '+100', {
        fontFamily: 'Segoe UI, sans-serif',
        fontSize: '32px',
        fontWeight: 'bold',
        color: '#00fff2',
        stroke: '#000000',
        strokeThickness: 5
      }).setOrigin(0.5);

      const subText = this.scene.add.text(175, 180, 'Electricidad reestablecida', {
        fontFamily: 'Segoe UI, sans-serif',
        fontSize: '18px',
        fontWeight: 'bold',
        color: '#ffffff',
        stroke: '#000000',
        strokeThickness: 4
      }).setOrigin(0.5);

      this.scene.tweens.add({
        targets: [pointsText, subText],
        y: '-=30',
        alpha: 0,
        duration: 1600,
        ease: 'Power2'
      });
    },

    skipLevel() {
  if (this.gameState !== "PLAYING") {
    return;
  }

  this.statsInternas.saltos++;


  this.score = Math.max(0, this.score - 100);

  this.createLevel();
},

    async endGame() {
      clearInterval(this.timerInterval);
      this.statsInternas.totalTimeMs = Date.now() - this.statsInternas.startTime;

      if (!this.modoPrueba && this.claveTemporal) {
        const stats = {
          pantallas_completadas: this.statsInternas.pantallas,
          total_clicks: this.statsInternas.clicks,
          niveles_saltados: this.statsInternas.saltos,
          tiempo_jugado_ms: this.statsInternas.totalTimeMs,
          puntos_locales: this.score
        };

        const statsOrdenados = Object.keys(stats).sort().reduce((obj, key) => {
          obj[key] = stats[key]; return obj;
        }, {});
        const statsStr = JSON.stringify(statsOrdenados);
        const hash = CryptoJS.HmacSHA256(statsStr, this.claveTemporal).toString(CryptoJS.enc.Hex);

        try {
          const response = await axios.post('/api/minigames/partida/finalizar', {
            id_partida: this.idPartida, stats: stats, hash: hash
          });
          if (response.data.status === 'success') {
            this.verifiedScore = response.data.data.score;
            this.verifiedMoney = response.data.data.dinero;

            await Promise.all([
              this.store.refreshStats(),
              this.store.refreshCompany(),
            ]);
          }
        } catch (error) {
          console.error("Error al validar partida", error);
        }
      }

      // Pasar a la pantalla final de resumen
      this.gameState = 'PARTIDA_TERMINADA';
    },

    resetGame() {
      this.gameState = 'MENU';
      this.score = 0;
      this.timeLeft = TIME_LEFT_CONST;
      this.verifiedScore = null;
      this.verifiedMoney = null;

      if (this.scene) this.scene.children.removeAll();
    },

    cleanup() {
      clearInterval(this.timerInterval);
      if (this.game) {
        this.game.destroy(true);
        this.game = null;
      }
    }
  }
};
</script>

<style scoped>
.power-grid-wrapper {
  width: 100%;
  max-width: 400px;
  height: 580px;
  margin: 0 auto;
  position: relative;
  background: #05050a;
  border: 4px solid #1a1a2e;
  border-radius: 20px;
  overflow: hidden;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

#game-container {
  width: 100%;
  height: 350px;
  margin-top: 80px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.overlay {
  position: absolute;
  inset: 0;
  z-index: 100;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: white;
  padding: 20px;
}

.menu-background {
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

.menu-content {
  background: rgba(0, 0, 0, 0.85);
  padding: 20px;
  border-radius: 15px;
  border: 1px solid #00fff2;
  width: 90%;
  max-width: 320px;
  box-sizing: border-box;
}

.summary-content {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.summary-box {
  margin-top: 15px;
  width: 100%;
}

.summary-box h2 {
  font-size: 1.1rem;
  color: #ff4444;
  margin: 5px 0 10px 0;
}

.summary-score {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 10px;
}

.summary-score span {
  font-size: 0.75rem;
  color: #a0aec0;
  letter-spacing: 1px;
}

.summary-score strong {
  font-size: 2.2rem;
  color: #00fff2;
  line-height: 1;
}

.summary-money {
  color: #38bdf8;
  font-size: 0.95rem;
  margin-bottom: 10px;
}

.houses-container {
  margin-top: 10px;
  background: rgba(255, 255, 255, 0.05);
  padding: 10px;
  border-radius: 10px;
}

.houses-title {
  font-size: 0.85rem;
  margin-bottom: 8px;
  color: #e2e8f0;
}

.houses-grid {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 6px;
  max-height: 90px;
  overflow-y: auto;
}

.house-icon {
  font-size: 1.3rem;
  animation: popIn 0.3s ease-out;
}

.no-houses {
  font-size: 0.8rem;
  color: #94a3b8;
  font-style: italic;
}

@keyframes popIn {
  from { transform: scale(0); }
  to { transform: scale(1); }
}

.test-mode-msg {
  font-size: 0.75rem;
  color: #ffaa00;
  margin: 8px 0;
}

.hud {
  position: absolute;
  top: 20px;
  width: 100%;
  display: flex;
  justify-content: space-around;
  z-index: 50;
}

.stat {
  background: rgba(22, 33, 62, 0.9);
  color: #00fff2;
  padding: 8px 20px;
  border-radius: 30px;
  font-weight: bold;
  border: 2px solid #0f3460;
  min-width: 100px;
  text-align: center;
}

.timer-low {
  color: #ff4444;
  border-color: #ff4444;
  animation: blink 1s infinite;
}

@keyframes blink { 50% { opacity: 0.5; } }

.btn-start {
  background: #00fff2;
  border: none;
  padding: 12px 30px;
  font-size: 1.1rem;
  font-weight: 800;
  border-radius: 50px;
  cursor: pointer;
  margin-top: 15px;
  color: #05050a;
  transition: transform 0.2s;
}

.btn-start:hover { transform: scale(1.05); }

.footer-controls {
  position: absolute;
  bottom: 30px;
  width: 100%;
  text-align: center;
}

.small-btn {
  background: rgba(10, 10, 18, 0.8);
  color: #00fff2;
  border: 2px solid #00fff2;
  padding: 10px 20px;
  border-radius: 10px;
  cursor: pointer;
  font-weight: bold;
  transition: all 0.3s;
}
</style>